import { DeleteOutlined } from '@ant-design/icons';
import { Input, Select } from 'antd';
import { useFormikContext } from 'formik';
import { T, TF } from 'react-translator-component';

import {
  countFilledHeaders,
  DoorFormValues,
  emptyHeaderRow,
  switchTransport,
} from './doorAccessMapping';
import { DoorFormErrors } from './doorValidation';
import {
  BodyEditor,
  Field,
  FieldError,
  FieldHelp,
  FieldsRow,
  HeaderRows,
  RequestRow,
} from './styled';

import { DOOR_TIMEOUT_MS } from '../../../../hooks/doorAccess.types';
import {
  DoorHeader,
  DoorHttpMethod,
  DoorTransport,
} from '../../../../types/responses/DoorAccess';
import { FormLabel } from '../styled';

const METHODS: DoorHttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

interface RequestBuilderProps {
  showErrors: boolean;
}

const RequestBuilder = ({ showErrors }: RequestBuilderProps) => {
  const { values, initialValues, errors, setFieldValue, setValues } =
    useFormikContext<DoorFormValues>();
  const formErrors = errors as DoorFormErrors;
  const moveIot = values.transport === 'MOVE_IOT';
  const rows = [...values.headers, emptyHeaderRow()];

  const updateRow = (index: number, patch: Partial<DoorHeader>) => {
    const headers = [...values.headers];
    headers[index] = { ...(headers[index] ?? emptyHeaderRow()), ...patch };
    setFieldValue('headers', headers);
  };

  const removeRow = (index: number) =>
    setFieldValue(
      'headers',
      values.headers.filter((_, i) => i !== index),
    );

  return (
    <>
      <FieldsRow $single={moveIot}>
        <Field>
          <FormLabel>{T('Opening method')}*:</FormLabel>
          <Select
            size='large'
            value={values.transport}
            onChange={(transport: DoorTransport) =>
              setValues(switchTransport(values, transport))
            }
          >
            <Select.Option value='HTTP'>
              {T('HTTP call (webhook)')}
            </Select.Option>
            <Select.Option value='MOVE_IOT'>movE IoT</Select.Option>
          </Select>
        </Field>

        {moveIot && (
          <Field>
            <FormLabel>{T('Device ID')}*:</FormLabel>
            <Input
              size='large'
              placeholder={TF('Enter the controller DEVICE_ID')}
              value={values.deviceId}
              maxLength={64}
              className={
                showErrors && formErrors.deviceId ? 'door-error' : undefined
              }
              onChange={(e) => setFieldValue('deviceId', e.target.value)}
            />
            {showErrors && formErrors.deviceId && (
              <FieldError>{T(formErrors.deviceId)}</FieldError>
            )}
          </Field>
        )}

        {!moveIot && (
          <Field>
            <FormLabel>{T('HTTP method')}*:</FormLabel>
            <Select
              size='large'
              value={values.method}
              onChange={(method) => setFieldValue('method', method)}
            >
              {METHODS.map((method) => (
                <Select.Option key={method} value={method}>
                  {method}
                </Select.Option>
              ))}
            </Select>
          </Field>
        )}

        {!moveIot && (
          <Field>
            <FormLabel>{T('Controller URL')}*:</FormLabel>
            <Input
              size='large'
              placeholder={TF('Enter the controller URL')}
              value={values.url}
              maxLength={2048}
              className={
                showErrors && formErrors.url ? 'door-error' : undefined
              }
              onChange={(e) => setFieldValue('url', e.target.value)}
            />
            {showErrors && formErrors.url && (
              <FieldError>{T(formErrors.url)}</FieldError>
            )}
          </Field>
        )}

        <Field>
          <FormLabel>{T('Timeout')}:</FormLabel>
          <Input
            size='large'
            disabled
            value={`${DOOR_TIMEOUT_MS / 1000} s`}
            title={TF('{0} s (system default)', DOOR_TIMEOUT_MS / 1000)}
          />
        </Field>
      </FieldsRow>

      {!moveIot && (
        <RequestRow>
          <Field>
            <FormLabel>
              {T('Headers')} ({countFilledHeaders(values.headers)}):
            </FormLabel>
            <HeaderRows>
              {rows.map((row, index) => {
                const rowErrors = showErrors
                  ? formErrors.headers?.[index]
                  : undefined;

                return (
                  <div key={index}>
                    <div className='row'>
                      <Input
                        size='large'
                        placeholder={TF('Key')}
                        value={row.key}
                        className={rowErrors?.key ? 'door-error' : undefined}
                        onChange={(e) =>
                          updateRow(index, { key: e.target.value })
                        }
                      />
                      <Input
                        size='large'
                        placeholder={TF('Value')}
                        value={row.value}
                        className={rowErrors?.value ? 'door-error' : undefined}
                        onChange={(e) =>
                          updateRow(index, { value: e.target.value })
                        }
                      />
                      {index < values.headers.length ? (
                        <button
                          type='button'
                          className='remove'
                          aria-label={TF('Remove header')}
                          title={TF('Remove header')}
                          onClick={() => removeRow(index)}
                        >
                          <DeleteOutlined />
                        </button>
                      ) : (
                        <span />
                      )}
                    </div>
                    {(rowErrors?.key || rowErrors?.value) && (
                      <FieldError>
                        {T((rowErrors.key || rowErrors.value) as string)}
                      </FieldError>
                    )}
                  </div>
                );
              })}
            </HeaderRows>
          </Field>

          <Field>
            <FormLabel>{T('JSON body')}:</FormLabel>
            <BodyEditor>
              <Input.TextArea
                style={{ height: 164 }}
                placeholder={TF('Enter the request body in JSON')}
                value={values.body}
                wrap='off'
                className={
                  (showErrors || values.body !== initialValues.body) &&
                  formErrors.body
                    ? 'door-error'
                    : undefined
                }
                onChange={(e) => setFieldValue('body', e.target.value)}
              />
            </BodyEditor>
            {(showErrors || values.body !== initialValues.body) &&
              formErrors.body && <FieldError>{T(formErrors.body)}</FieldError>}
            <FieldHelp>
              {T('Variables:')} <code>{'{stationId}'}</code> ·{' '}
              <code>{'{connectorId}'}</code> · <code>{'{requestId}'}</code>
            </FieldHelp>
          </Field>
        </RequestRow>
      )}
    </>
  );
};

export default RequestBuilder;
